import {
  NOTIFICATION_SAVED,
  NOTIFICATION_ERROR,
  NOTIFICATION_RESET,
  UPDATE_ACTION_ID,
  UPDATE_DIALOG_TEXT,
  NOTIFICATION_REMOVED,
  DEFERRED_STATUS_TEXT,
} from '@web-platform/shared-universal-types/src/saveToList';

import {
  NOTIFICATION_SAVED_WITH_STATUS_TEXT,
  RESET_DIALOG_TEXT,
  SAVE_ITINERARY_WHILE_LOGGED_OUT,
  LOGIN_FLOW_COMPLETED,
  SAVE_LIMIT_ERROR_DIALOG_DISMISSED,
  TOGGLE_PRICE_ALERT_INFO,
} from '../../../actions/saveToList';

import {
  HEART_SAVED_NOTIFICATION,
  HEART_REMOVED_NOTIFICATION,
  HEART_ERROR_NOTIFICATION,
} from './constants';

import type {
  TogglePriceAlertInfo,
  SaveItineraryWhileLoggedOutAction,
  LoginFlowCompletedAction,
  SaveLimitErrorDialogDismissedAction,
} from '@web-platform/shared-universal-types/src/redux/acorn/actions/actions-acorn-types';
import type {
  SaveToListNotificationSavedAction,
  SaveToListNotificationResetAction,
  SaveToListNotificationErrorAction,
  SaveToListUpdateActionIdAction,
  SaveToListUpdateDialogTextAction,
  SaveToListNotificationRemovedAction,
  SaveToListDeferredStatusTextAction,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  SaveToListNotificationSavedWithStatusTextAction,
  SaveToListResetDialogTextAction,
} from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type {
  S2LErrorDialog,
  S2LNotification,
  SaveToListState,
} from '@web-platform/shared-universal-types/src/redux/state/shared/saveToList';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

const getInitialState = (): SaveToListState => ({
  lastUpdated: {
    actionId: null,
  },
  notification: null,
  statusText: null,
  tripId: null,
  errorDialog: {
    headingText: '',
    subHeadingText: '',
  },
  priceAlertInfoIsOpen: false,
  itineraryIdToSaveOnLogIn: null,
});

type SaveToListAction =
  | SaveToListNotificationSavedAction
  | SaveToListNotificationSavedWithStatusTextAction
  | SaveToListNotificationRemovedAction
  | SaveToListNotificationErrorAction
  | SaveToListNotificationResetAction
  | SaveToListUpdateActionIdAction
  | SaveToListUpdateDialogTextAction
  | SaveToListResetDialogTextAction
  | SaveToListDeferredStatusTextAction
  | SaveItineraryWhileLoggedOutAction
  | LoginFlowCompletedAction
  | SaveLimitErrorDialogDismissedAction
  | TogglePriceAlertInfo;

const SAVE_TO_LIST_ACTION_TYPES: ReadonlySet<string> = new Set([
  NOTIFICATION_SAVED,
  NOTIFICATION_ERROR,
  NOTIFICATION_RESET,
  UPDATE_ACTION_ID,
  UPDATE_DIALOG_TEXT,
  NOTIFICATION_REMOVED,
  DEFERRED_STATUS_TEXT,
  NOTIFICATION_SAVED_WITH_STATUS_TEXT,
  RESET_DIALOG_TEXT,
  SAVE_ITINERARY_WHILE_LOGGED_OUT,
  LOGIN_FLOW_COMPLETED,
  SAVE_LIMIT_ERROR_DIALOG_DISMISSED,
  TOGGLE_PRICE_ALERT_INFO,
]);

const isSaveToListAction = (action: { type: string }): action is SaveToListAction =>
  SAVE_TO_LIST_ACTION_TYPES.has(action.type);

export const saveToListReducer = (
  state: SaveToListState = getInitialState(),
  action: { type: string },
): SaveToListState => {
  if (!isSaveToListAction(action)) {
    return state;
  }

  switch (action.type) {
    case SAVE_ITINERARY_WHILE_LOGGED_OUT:
      return {
        ...state,
        itineraryIdToSaveOnLogIn: action.payload.itineraryId,
      };
    case LOGIN_FLOW_COMPLETED:
      return {
        ...state,
        itineraryIdToSaveOnLogIn: null,
      };
    case NOTIFICATION_SAVED_WITH_STATUS_TEXT:
      return {
        ...state,
        notification: {
          state: HEART_SAVED_NOTIFICATION,
          source: action.payload.source,
        },
        statusText: action.payload.statusText,
        tripId: null,
      };
    case NOTIFICATION_SAVED: {
      const { source, statusText, tripId } = action.payload;

      if (statusText) {
        return {
          ...state,
          notification: {
            state: HEART_SAVED_NOTIFICATION,
            source,
          },
          statusText,
          tripId: tripId ?? null,
        };
      }
      return {
        ...state,
        notification: {
          state: HEART_SAVED_NOTIFICATION,
          source,
        },
        tripId: null,
      };
    }
    case NOTIFICATION_REMOVED:
      return {
        ...state,
        notification: {
          state: HEART_REMOVED_NOTIFICATION,
          source: action.payload.source,
        },
        statusText: action.payload.statusText,
        tripId: null,
      };
    case NOTIFICATION_RESET:
      return { ...state, notification: null, statusText: null, tripId: null };
    case NOTIFICATION_ERROR:
      return {
        ...state,
        notification: {
          state: HEART_ERROR_NOTIFICATION,
          source: action.payload.source,
        },
        statusText: null,
        tripId: null,
      };
    case UPDATE_ACTION_ID:
      return {
        ...state,
        lastUpdated: { ...state.lastUpdated, actionId: action.payload.actionId },
      };
    case UPDATE_DIALOG_TEXT:
      return {
        ...state,
        errorDialog: {
          headingText: action.payload.headingText,
          subHeadingText: action.payload.subHeadingText,
        },
      };
    case RESET_DIALOG_TEXT:
    case SAVE_LIMIT_ERROR_DIALOG_DISMISSED:
      return {
        ...state,
        errorDialog: {
          headingText: '',
          subHeadingText: '',
        },
      };
    case DEFERRED_STATUS_TEXT:
      return {
        ...state,
        statusText: action.payload.statusText,
      };
    case TOGGLE_PRICE_ALERT_INFO:
      return {
        ...state,
        priceAlertInfoIsOpen: !state.priceAlertInfoIsOpen,
      };
    default:
      return state;
  }
};

export default saveToListReducer;

export const getS2lNotification = (state: SaveToListState): Nullable<S2LNotification> =>
  state.notification;
export const getLastUpdatedActionId = (state: SaveToListState): Nullable<string> =>
  state.lastUpdated.actionId;
export const getStatusText = (state: SaveToListState): Nullable<string> => state.statusText;
export const getTripId = (state: SaveToListState): Nullable<string> => state.tripId;
export const getErrorDialogText = (state: SaveToListState): S2LErrorDialog => state.errorDialog;
export const getPriceAlertInfoIsOpen = (state: SaveToListState): boolean =>
  state.priceAlertInfoIsOpen;
export const getItineraryIdToSaveOnLogIn = (state: SaveToListState): Nullable<string> =>
  state.itineraryIdToSaveOnLogIn;
